<template>
  <AppHero
    kicker="Responsabilidade"
    title="Equipamentos sob sua responsabilidade"
    title-tag="h3"
    description="Termos de guarda e uso dos equipamentos vinculados ao seu login de rede. A assinatura é eletrônica: você lê o documento e confirma com a mesma senha que usa para entrar no computador."
  >
    <template #aside>
      <div class="termo-hero-resumo">
        <article class="termo-hero-metrica" :class="{ 'is-alerta': pendentes.length > 0 }">
          <span class="termo-hero-metrica-valor">{{ pendentes.length }}</span>
          <span class="termo-hero-metrica-rotulo">Aguardando assinatura</span>
        </article>
        <article class="termo-hero-metrica">
          <span class="termo-hero-metrica-valor">{{ totalAssinados }}</span>
          <span class="termo-hero-metrica-rotulo">Já assinados</span>
        </article>
      </div>
    </template>

    <div class="card tab-content text-muted termo-card">
      <div class="tab-pane active p-2" role="tabpanel">
        <div v-if="carregando" class="termo-estado">
          <BSpinner type="grow" variant="primary" />
          <span>Carregando seus termos...</span>
        </div>

        <template v-else>
          <div class="termo-bloco">
            <div class="termo-bloco-cabecalho">
              <h6 class="mb-0">Pendentes de assinatura</h6>
              <span v-if="pendentes.length > 0" class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle">
                {{ pendentes.length }} pendente(s)
              </span>
            </div>

            <div v-if="pendentes.length === 0" class="termo-vazio">
              <i class="ri-checkbox-circle-line text-success"></i>
              <div>
                <strong>Tudo em dia.</strong>
                <span>Você não tem termos aguardando assinatura no momento.</span>
              </div>
            </div>

            <article v-for="item in pendentes" :key="item.id" class="termo-item">
              <div class="termo-item-icone"><i class="ri-file-shield-2-line"></i></div>

              <div class="termo-item-conteudo">
                <span class="termo-item-kicker">{{ item.Modelo?.titulo || "Termo de responsabilidade" }}</span>
                <h6 class="termo-item-titulo">
                  {{ item.snapshot_equipamento?.hostname || item.Host?.hostname || "Equipamento" }}
                </h6>
                <div class="termo-item-meta">
                  <span>
                    <i class="ri-price-tag-3-line"></i>
                    Patrimônio {{ item.snapshot_equipamento?.patrimonio || "—" }}
                  </span>
                  <span v-if="item.snapshot_equipamento?.modelo">
                    <i class="ri-computer-line"></i>
                    {{ [item.snapshot_equipamento.fabricante, item.snapshot_equipamento.modelo].filter(Boolean).join(" ") }}
                  </span>
                  <span>
                    <i class="ri-calendar-line"></i>
                    Gerado em {{ formatarData(item.gerado_em) }}
                  </span>
                  <span v-if="item.prazo_em" :class="vencido(item) ? 'text-danger fw-semibold' : ''">
                    <i class="ri-alarm-line"></i>
                    Prazo {{ formatarData(item.prazo_em) }}
                  </span>
                </div>
                <p v-if="item.motivo_reemissao" class="termo-item-nota">
                  <i class="ri-information-line"></i>{{ item.motivo_reemissao }}
                </p>
              </div>

              <div class="termo-item-acao">
                <BButton variant="primary" @click="abrir(item)">
                  <i class="ri-quill-pen-line align-bottom me-1"></i>Ler e assinar
                </BButton>
              </div>
            </article>
          </div>

          <div v-if="historico.length > 0" class="termo-bloco">
            <div class="termo-bloco-cabecalho">
              <h6 class="mb-0">Histórico</h6>
              <span class="text-muted small">{{ historico.length }} registro(s)</span>
            </div>

            <div class="table-responsive">
              <table class="table table-striped table-hover align-middle table-nowrap mb-0">
                <thead class="table-light">
                  <tr class="text-muted">
                    <th>Equipamento</th>
                    <th class="d-none d-md-table-cell">Termo</th>
                    <th>Situação</th>
                    <th class="d-none d-lg-table-cell">Data</th>
                    <th class="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in historico" :key="item.id">
                    <td>
                      <span class="text-primary fw-medium termo-link" @click="abrir(item)">
                        {{ item.snapshot_equipamento?.hostname || item.Host?.hostname || "—" }}
                      </span>
                      <div class="text-muted small">
                        Patrimônio {{ item.snapshot_equipamento?.patrimonio || "—" }}
                      </div>
                    </td>
                    <td class="d-none d-md-table-cell">
                      <div>{{ item.Modelo?.titulo || "—" }}</div>
                      <div class="text-muted small">versão {{ item.Versao?.versao || "—" }}</div>
                    </td>
                    <td>
                      <span class="badge fs-6" :class="statusVariante(item.status)">{{ statusTexto(item.status) }}</span>
                    </td>
                    <td class="d-none d-lg-table-cell">
                      {{ formatarData(item.aceito_em || item.recusado_em || item.gerado_em) }}
                    </td>
                    <td class="text-end">
                      <a
                        class="btn btn-sm btn-soft-secondary me-1"
                        :href="urlPdf(item)"
                        target="_blank"
                        rel="noopener"
                        title="Baixar comprovante"
                      >
                        <i class="ri-file-pdf-2-line"></i>
                      </a>
                      <button class="btn btn-sm btn-soft-primary" title="Ver termo" @click="abrir(item)">
                        <i class="ri-eye-line"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </div>

    <BModal
      v-model="modalAberto"
      no-close-on-backdrop
      size="xl"
      centered
      hide-footer
      class="v-modal-custom"
      header-close-class="text-white bg-white"
      title-class="text-white font-18"
      header-class="p-3 bg-primary"
      body-class="p-4"
      dialog-class="modal-xxl modal-fullscreen-lg-down"
      :title="`Termo #${detalhe?.id || ''} - ${detalhe?.Modelo?.titulo || 'Termo de responsabilidade'}`"
    >
      <div v-if="carregandoDetalhe" class="termo-estado">
        <BSpinner type="grow" variant="primary" />
        <span>Abrindo o documento...</span>
      </div>

      <div v-else-if="detalhe">
        <div class="termo-resumo">
          <div>
            <span class="termo-resumo-rotulo">Equipamento</span>
            <strong>{{ detalhe.snapshot_equipamento?.hostname || "—" }}</strong>
          </div>
          <div>
            <span class="termo-resumo-rotulo">Patrimônio</span>
            <strong>{{ detalhe.snapshot_equipamento?.patrimonio || "—" }}</strong>
          </div>
          <div>
            <span class="termo-resumo-rotulo">Número de série</span>
            <strong>{{ detalhe.snapshot_equipamento?.serial_number || "—" }}</strong>
          </div>
          <div>
            <span class="termo-resumo-rotulo">Situação</span>
            <strong>{{ statusTexto(detalhe.status) }}</strong>
          </div>
        </div>

        <div class="termo-documento conteudo-rico" v-html="documentoSeguro"></div>

        <div v-if="detalhe.status === 'pendente'" class="termo-assinatura">
          <div class="form-check mb-3">
            <input id="termo-ciente" v-model="ciente" class="form-check-input" type="checkbox" />
            <label class="form-check-label" for="termo-ciente">
              Li o termo por completo, compreendi e concordo com as responsabilidades descritas.
            </label>
          </div>

          <BRow class="g-3 align-items-end">
            <BCol md="6">
              <label class="form-label">Senha da sua conta de rede</label>
              <input
                v-model="senha"
                type="password"
                class="form-control"
                autocomplete="current-password"
                placeholder="A mesma senha usada para entrar no computador"
                @keyup.enter="assinar"
              />
              <small class="text-muted">
                A senha é usada apenas para confirmar sua identidade e não é armazenada.
              </small>
            </BCol>
            <BCol md="6" class="d-flex flex-wrap gap-2 justify-content-md-end">
              <BButton v-if="!recusando" variant="light" :disabled="assinando" @click="abrirRecusa">
                <i class="ri-close-circle-line align-bottom me-1"></i>Recusar
              </BButton>
              <BButton variant="success" :disabled="assinando || !ciente || !senha" @click="assinar">
                <span v-if="assinando" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="ri-quill-pen-line align-bottom me-1"></i>
                Assinar termo
              </BButton>
            </BCol>
          </BRow>

          <div v-if="recusando" class="termo-recusa mt-3">
            <label class="form-label">Motivo da recusa <span class="text-danger">*</span></label>
            <textarea
              ref="campoMotivoRecusa"
              v-model="motivoRecusa"
              rows="3"
              class="form-control"
              maxlength="2000"
              placeholder="Ex.: este equipamento não está comigo; o patrimônio informado não confere..."
            ></textarea>
            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2">
              <small class="text-muted">{{ motivoRecusa.trim().length }}/10 caracteres mínimos</small>
              <div class="d-flex flex-wrap gap-2">
                <BButton variant="light" :disabled="assinando" @click="recusando = false">Voltar</BButton>
                <BButton
                  variant="danger"
                  :disabled="assinando || motivoRecusa.trim().length < 10"
                  @click="recusar"
                >
                  <span v-if="assinando" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="ri-close-circle-line align-bottom me-1"></i>
                  Confirmar recusa
                </BButton>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="termo-assinado">
          <i class="ri-shield-check-line"></i>
          <span v-if="detalhe.status === 'aceito'">
            Assinado eletronicamente em {{ formatarDataHora(detalhe.aceito_em) }}.
            Código de verificação: <code>{{ (detalhe.assinatura_hash || "").slice(0, 16) }}</code>
          </span>
          <span v-else>Situação atual: {{ statusTexto(detalhe.status) }}.</span>
        </div>
      </div>
    </BModal>
  </AppHero>
</template>

<script>
import axios from "axios";
import AppHero from "@/components/mirai/AppHero.vue";
import { confirmarAsync, erro, sucesso } from "@/helpers/mirai/custom";
import { sanitizarHtml } from "@/helpers/mirai/html";

const STATUS_TEXTO = {
  pendente: "Pendente",
  aceito: "Assinado",
  recusado: "Recusado",
  cancelado: "Cancelado",
  expirado: "Expirado",
};

export default {
  name: "TermosSection",
  components: { AppHero },
  // O contador sobe para o painel: o atalho do topo e o aviso de pendência
  // precisam cair no mesmo instante em que o usuário assina.
  emits: ["pendentes"],
  data() {
    return {
      carregando: false,
      carregandoDetalhe: false,
      assinando: false,
      pendentes: [],
      historico: [],
      modalAberto: false,
      detalhe: null,
      ciente: false,
      senha: "",
      recusando: false,
      motivoRecusa: "",
    };
  },
  computed: {
    totalAssinados() {
      return this.historico.filter((item) => item.status === "aceito").length;
    },
    // O snapshot vem do editor do admin e é renderizado com v-html: passa pela
    // allowlist antes de ir para a tela.
    documentoSeguro() {
      const html = sanitizarHtml(this.detalhe?.snapshot_html || "");
      return html || "<p>Conteúdo do termo indisponível.</p>";
    },
  },
  created() {
    this.carregar();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      try {
        const resposta = await axios.get("/termoaceite/meus");
        this.pendentes = resposta.data?.pendentes || [];
        this.historico = resposta.data?.historico || [];
      } catch (e) {
        erro({ text: e?.response?.data?.error || "Não foi possível carregar seus termos." });
        this.pendentes = [];
        this.historico = [];
      } finally {
        this.carregando = false;
        this.$emit("pendentes", this.pendentes.length);
      }
    },
    async abrir(item) {
      this.detalhe = null;
      this.ciente = false;
      this.senha = "";
      this.recusando = false;
      this.motivoRecusa = "";
      this.modalAberto = true;
      this.carregandoDetalhe = true;
      try {
        const resposta = await axios.get("/termoaceite/detalhe", { params: { id: item.id } });
        this.detalhe = resposta.data || null;
      } catch (e) {
        erro({ text: e?.response?.data?.error || "Não foi possível abrir o termo." });
        this.modalAberto = false;
      } finally {
        this.carregandoDetalhe = false;
      }
    },
    async assinar() {
      if (!this.ciente) {
        erro({ title: "Confirme a leitura", text: "Marque a caixa de ciência antes de assinar." });
        return;
      }
      if (!this.senha) {
        erro({ title: "Informe a senha", text: "A senha da rede confirma que é você assinando." });
        return;
      }

      this.assinando = true;
      try {
        const resposta = await axios.post("/termoaceite/assinar", {
          id: this.detalhe.id,
          senha: this.senha,
          ciente: true,
        });
        this.senha = "";
        sucesso({
          title: "Termo assinado",
          text: `Código de verificação: ${(resposta.data?.assinatura_hash || "").slice(0, 16)}`,
          showConfirmButton: true,
          timer: false,
        });
        this.modalAberto = false;
        await this.carregar();
      } catch (e) {
        erro({ text: e?.response?.data?.error || "Não foi possível registrar a assinatura." });
      } finally {
        this.assinando = false;
      }
    },
    abrirRecusa() {
      this.recusando = true;
      this.$nextTick(() => this.$refs.campoMotivoRecusa?.focus?.());
    },
    async recusar() {
      const motivo = this.motivoRecusa.trim();
      if (motivo.length < 10) {
        erro({ title: "Motivo muito curto", text: "Descreva o motivo com pelo menos 10 caracteres." });
        return;
      }

      const confirmado = await confirmarAsync({
        title: "Recusar o termo?",
        text: "A recusa é registrada e encaminhada à equipe de TI para tratativa.",
        confirmButtonText: "Sim, recusar",
        cancelButtonText: "Voltar",
        variant: "danger",
      });
      if (!confirmado) return;

      this.assinando = true;
      try {
        await axios.post("/termoaceite/recusar", { id: this.detalhe.id, motivo });
        sucesso({ title: "Recusa registrada", text: "A equipe de TI foi notificada." });
        this.recusando = false;
        this.motivoRecusa = "";
        this.modalAberto = false;
        await this.carregar();
      } catch (e) {
        erro({ text: e?.response?.data?.error || "Não foi possível registrar a recusa." });
      } finally {
        this.assinando = false;
      }
    },
    vencido(item) {
      return item.prazo_em && new Date(item.prazo_em) < new Date();
    },
    formatarData(valor) {
      if (!valor) return "—";
      const data = new Date(valor);
      if (Number.isNaN(data.getTime())) return "—";
      return data.toLocaleDateString("pt-BR");
    },
    formatarDataHora(valor) {
      if (!valor) return "—";
      const data = new Date(valor);
      if (Number.isNaN(data.getTime())) return "—";
      return data.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    statusTexto(status) {
      return STATUS_TEXTO[status] || status;
    },
    statusVariante(status) {
      return {
        pendente: "bg-warning-subtle text-warning-emphasis border border-warning-subtle",
        aceito: "bg-success-subtle text-success-emphasis border border-success-subtle",
        recusado: "bg-danger-subtle text-danger-emphasis border border-danger-subtle",
        cancelado: "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle",
        expirado: "bg-dark-subtle text-dark-emphasis border border-dark-subtle",
      }[status] || "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle";
    },
    urlPdf(item) {
      const base = axios.defaults.baseURL || "";
      return `${base.replace(/\/+$/, "")}/termoaceite/pdf?id=${item.id}`;
    },
  },
};
</script>

<style scoped>
/* Métricas do aside do hero, no mesmo tom dos painéis claros da home. */
.termo-hero-resumo {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.termo-hero-metrica {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.9rem 1rem;
  border: 1px solid var(--app-hero-chip-border, rgba(37, 99, 235, 0.14));
  border-radius: 1rem;
  background: var(--app-hero-panel-strong, rgba(255, 255, 255, 0.92));
}

.termo-hero-metrica.is-alerta {
  border-color: rgba(245, 158, 11, 0.34);
  background: rgba(245, 158, 11, 0.1);
}

.termo-hero-metrica-valor {
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.1;
  color: var(--app-hero-title, #0f172a);
}

.termo-hero-metrica-rotulo {
  font-size: 0.76rem;
  color: var(--app-hero-muted, #64748b);
}

.termo-card {
  border-radius: 1rem;
  box-shadow: none;
}

.termo-estado {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  color: var(--vz-secondary-color, #64748b);
}

.termo-bloco + .termo-bloco {
  margin-top: 1.75rem;
}

.termo-bloco-cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.35rem 0.35rem 0.75rem;
}

.termo-bloco-cabecalho h6 {
  font-weight: 700;
  color: var(--vz-body-color, #0f172a);
}

.termo-vazio {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.15rem 1.25rem;
  border: 1px dashed rgba(148, 163, 184, 0.32);
  border-radius: 0.9rem;
  color: var(--vz-secondary-color, #64748b);
}

.termo-vazio i {
  font-size: 1.5rem;
}

.termo-vazio strong {
  display: block;
  color: var(--vz-body-color, #0f172a);
}

.termo-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-left: 4px solid #f59e0b;
  border-radius: 1rem;
  background: var(--vz-card-bg, #fff);
}

.termo-item + .termo-item {
  margin-top: 0.75rem;
}

.termo-item-icone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.9rem;
  background: rgba(245, 158, 11, 0.14);
  color: #b45309;
  font-size: 1.35rem;
}

.termo-item-conteudo {
  flex: 1 1 320px;
  min-width: 0;
}

.termo-item-kicker {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1d4ed8;
}

.termo-item-titulo {
  margin: 0.15rem 0 0.45rem;
  font-weight: 700;
  color: var(--vz-body-color, #0f172a);
}

.termo-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  font-size: 0.82rem;
  color: var(--vz-secondary-color, #64748b);
}

.termo-item-meta i {
  margin-right: 0.25rem;
}

.termo-item-nota {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #b45309;
}

.termo-link {
  cursor: pointer;
}

/* --- Modal --- */
.termo-resumo {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 0.9rem;
  background: var(--vz-tertiary-bg, #f8fafc);
}

.termo-resumo-rotulo {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--vz-secondary-color, #64748b);
}

.termo-documento {
  max-height: 52vh;
  overflow-y: auto;
  padding: 1.35rem;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 0.9rem;
  background: var(--vz-card-bg, #fff);
  line-height: 1.7;
}

.termo-assinatura {
  padding: 1.15rem;
  margin-top: 1rem;
  border: 1px solid rgba(13, 110, 253, 0.24);
  border-radius: 0.9rem;
  background: rgba(13, 110, 253, 0.05);
}

.termo-recusa {
  padding: 1rem;
  border: 1px solid rgba(239, 68, 68, 0.26);
  border-radius: 0.9rem;
  background: rgba(239, 68, 68, 0.06);
}

.termo-assinado {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 1.15rem;
  margin-top: 1rem;
  border: 1px solid rgba(34, 197, 94, 0.26);
  border-radius: 0.9rem;
  background: rgba(34, 197, 94, 0.08);
  color: #15803d;
  font-size: 0.9rem;
}

.termo-assinado i {
  font-size: 1.35rem;
}

@media (max-width: 767.98px) {
  .termo-item-acao,
  .termo-item-acao :deep(.btn) {
    width: 100%;
  }
}

[data-bs-theme="dark"] .termo-hero-metrica,
[data-layout-mode="dark"] .termo-hero-metrica {
  background: rgba(15, 23, 42, 0.7);
  border-color: rgba(148, 163, 184, 0.18);
}

[data-bs-theme="dark"] .termo-item,
[data-layout-mode="dark"] .termo-item {
  border-color: rgba(148, 163, 184, 0.18);
}

[data-bs-theme="dark"] .termo-vazio,
[data-layout-mode="dark"] .termo-vazio {
  border-color: rgba(148, 163, 184, 0.24);
}

[data-bs-theme="dark"] .termo-assinado,
[data-layout-mode="dark"] .termo-assinado {
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(34, 197, 94, 0.26);
  color: #86efac;
}

[data-bs-theme="dark"] .termo-assinatura,
[data-layout-mode="dark"] .termo-assinatura {
  background: rgba(13, 110, 253, 0.12);
  border-color: rgba(13, 110, 253, 0.28);
}

[data-bs-theme="dark"] .termo-recusa,
[data-layout-mode="dark"] .termo-recusa {
  background: rgba(239, 68, 68, 0.14);
  border-color: rgba(239, 68, 68, 0.3);
}
</style>
